import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../config/api';
import { UserAuthContext } from '../UserAuthContext';

const STORAGE_KEY = 'eulua_currency';
// Mirrors SUPPORTED_CURRENCIES in EULUAPI/services/currencies.js. Used only until
// /api/currencies answers with the codes that actually have a rate today.
const FALLBACK_CURRENCIES = [
  'USD', 'HNL', 'EUR', 'MXN', 'GTQ', 'BZD', 'SVC', 'NIO', 'CRC', 'PAB',
  'DOP', 'CUP', 'JMD', 'HTG', 'COP', 'VES', 'GYD', 'SRD', 'BRL', 'PEN',
  'BOB', 'PYG', 'UYU', 'ARS', 'CLP', 'CAD', 'GBP', 'CHF', 'CNY', 'JPY',
  'KRW', 'INR', 'AUD', 'NZD', 'AED', 'SAR', 'ZAR',
];
// Mirrors COUNTRY_CURRENCY in EULUAPI/services/currencies.js. It is only the first guess
// before /api/currencies replies with defaultCurrency for the visitor's country.
const COUNTRY_CURRENCY = {
  US: 'USD', 'UNITED STATES': 'USD', USA: 'USD', ESTADOSUNIDOS: 'USD',
  HN: 'HNL', HONDURAS: 'HNL', MX: 'MXN', MEXICO: 'MXN', 'MÉXICO': 'MXN',
  GT: 'GTQ', GUATEMALA: 'GTQ', BZ: 'BZD', BELIZE: 'BZD',
  SV: 'USD', 'EL SALVADOR': 'USD', NI: 'NIO', NICARAGUA: 'NIO',
  CR: 'CRC', 'COSTA RICA': 'CRC', PA: 'PAB', PANAMA: 'PAB', 'PANAMÁ': 'PAB',
  DO: 'DOP', 'DOMINICAN REPUBLIC': 'DOP', 'REPÚBLICA DOMINICANA': 'DOP',
  CU: 'CUP', CUBA: 'CUP', JM: 'JMD', JAMAICA: 'JMD', HT: 'HTG', HAITI: 'HTG', HAITÍ: 'HTG',
  CO: 'COP', COLOMBIA: 'COP', VE: 'VES', VENEZUELA: 'VES',
  GY: 'GYD', GUYANA: 'GYD', SR: 'SRD', SURINAME: 'SRD', BR: 'BRL', BRAZIL: 'BRL', BRASIL: 'BRL',
  PE: 'PEN', PERU: 'PEN', PERÚ: 'PEN', BO: 'BOB', BOLIVIA: 'BOB',
  PY: 'PYG', PARAGUAY: 'PYG', UY: 'UYU', URUGUAY: 'UYU',
  AR: 'ARS', ARGENTINA: 'ARS', CL: 'CLP', CHILE: 'CLP', CA: 'CAD', CANADA: 'CAD', CANADÁ: 'CAD',
  ES: 'EUR', SPAIN: 'EUR', ESPAÑA: 'EUR', FR: 'EUR', FRANCE: 'EUR', FRANCIA: 'EUR',
  DE: 'EUR', GERMANY: 'EUR', ALEMANIA: 'EUR', IT: 'EUR', ITALY: 'EUR', ITALIA: 'EUR',
  GB: 'GBP', UK: 'GBP', 'UNITED KINGDOM': 'GBP', 'REINO UNIDO': 'GBP',
  CH: 'CHF', SWITZERLAND: 'CHF', SUIZA: 'CHF', CN: 'CNY', CHINA: 'CNY',
  JP: 'JPY', JAPAN: 'JPY', JAPÓN: 'JPY', KR: 'KRW', 'SOUTH KOREA': 'KRW',
  IN: 'INR', INDIA: 'INR', AU: 'AUD', AUSTRALIA: 'AUD', NZ: 'NZD', 'NEW ZEALAND': 'NZD',
  AE: 'AED', 'UNITED ARAB EMIRATES': 'AED', SA: 'SAR', 'SAUDI ARABIA': 'SAR', ZA: 'ZAR', 'SOUTH AFRICA': 'ZAR',
};

const ZERO_DECIMAL_CURRENCIES = ['JPY', 'KRW', 'CLP', 'PYG'];

const normalize = (value, fallback = 'USD') => {
  const code = String(value || '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : fallback;
};

// Formats an amount in a fixed currency, with no conversion. Use it for figures that are
// billed or transferred exactly as stated: USD memberships, and bank deposits in the
// currency the order was created in.
export const formatIn = (amount, currencyCode = 'USD', options = {}) => {
  const value = Number(amount) || 0;
  const currency = normalize(currencyCode);
  const maximumFractionDigits = options.maximumFractionDigits
    ?? (ZERO_DECIMAL_CURRENCIES.includes(currency) ? 0 : 2);
  const preferredMinimum = options.minimumFractionDigits
    ?? (Number.isInteger(value) && options.trimWholeNumbers ? 0 : 2);
  return new Intl.NumberFormat(options.locale, {
    style: 'currency',
    currency,
    // Intl throws when the minimum exceeds the maximum, which happens on the currencies
    // that carry no decimals (CLP, PYG, JPY, KRW).
    minimumFractionDigits: Math.min(preferredMinimum, maximumFractionDigits),
    maximumFractionDigits,
  }).format(value);
};

// Memberships, advertising packages and the commission minimum are always billed in US
// dollars, so they are shown as a fixed USD figure rather than a converted equivalent.
export const formatUsd = (amount) => `US${formatIn(amount, 'USD', { locale: 'en-US', trimWholeNumbers: true })}`;

export const currencyForCountry = (country) => COUNTRY_CURRENCY[String(country || '').trim().toUpperCase()] || 'USD';
export const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const { user, location, updateCurrentUser } = useContext(UserAuthContext);
  const automaticCurrency = currencyForCountry(user?.country || location?.country);
  const [currency, setCurrencyState] = useState(() => normalize(localStorage.getItem(STORAGE_KEY) || user?.preferred_currency || automaticCurrency));
  const [currencies, setCurrencies] = useState(FALLBACK_CURRENCIES);
  const [rates, setRates] = useState({ USD: 1 });
  const [rateDate, setRateDate] = useState('');
  const [detectedCurrency, setDetectedCurrency] = useState('');
  const [ratesAvailable, setRatesAvailable] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return;
    // The API's defaultCurrency covers every country the backend knows; the local map is
    // only the first guess while that request is in flight.
    setCurrencyState(normalize(user?.preferred_currency || detectedCurrency || automaticCurrency));
  }, [automaticCurrency, detectedCurrency, user?.preferred_currency]);

  useEffect(() => {
    const controller = new AbortController();
    const country = user?.country || location?.country || '';
    fetch(apiUrl(`/api/currencies?country=${encodeURIComponent(country)}`), { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Currency rates unavailable');
        return response.json();
      })
      .then((data) => {
        setRates({ USD: 1, ...(data.rates || {}) });
        setCurrencies(data.currencies?.length ? data.currencies : FALLBACK_CURRENCIES);
        setRateDate(data.date || '');
        // Only trust defaultCurrency when a country was actually sent; otherwise the API
        // answers USD for everyone and would override the local guess.
        if (country) setDetectedCurrency(normalize(data.defaultCurrency, ''));
        setRatesAvailable(true);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setRatesAvailable(false);
      });
    return () => controller.abort();
  }, [location?.country, user?.country]);

  const setCurrency = useCallback(async (nextCurrency) => {
    const next = normalize(nextCurrency);
    setCurrencyState(next);
    localStorage.setItem(STORAGE_KEY, next);
    if (user && localStorage.getItem('token')) {
      updateCurrentUser?.({ preferred_currency: next });
      fetch(apiUrl('/api/account/currency'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ currency: next }),
      }).catch(() => {});
    }
  }, [updateCurrentUser, user]);

  const convert = useCallback((amount, sourceCurrency = 'USD', targetCurrency = currency) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return 0;
    const source = normalize(sourceCurrency);
    const target = normalize(targetCurrency);
    if (source === target) return numeric;
    const sourceRate = Number(rates[source]);
    const targetRate = Number(rates[target]);
    if (!(sourceRate > 0) || !(targetRate > 0)) return numeric;
    return (numeric / sourceRate) * targetRate;
  }, [currency, rates]);

  // Sums amounts that may be stored in different currencies. Returns total === null when any
  // entry cannot be converted, so callers never label a foreign figure with the active currency.
  const convertTotal = useCallback((entries = [], targetCurrency = currency) => {
    const target = normalize(targetCurrency);
    let total = 0;
    for (const entry of entries) {
      const amount = Number(entry?.amount);
      if (!Number.isFinite(amount)) continue;
      const source = normalize(entry?.currency);
      if (source === target) { total += amount; continue; }
      const sourceRate = Number(rates[source]);
      const targetRate = Number(rates[target]);
      if (!(sourceRate > 0) || !(targetRate > 0)) return { total: null, currency: target };
      total += (amount / sourceRate) * targetRate;
    }
    return { total, currency: target };
  }, [currency, rates]);

  const formatMoney = useCallback((amount, sourceCurrency = 'USD', options = {}) => {
    const source = normalize(sourceCurrency);
    const canConvert = source === currency || (Number(rates[source]) > 0 && Number(rates[currency]) > 0);
    const displayedCurrency = canConvert ? currency : source;
    const displayedAmount = canConvert ? convert(amount, source, displayedCurrency) : Number(amount || 0);
    return new Intl.NumberFormat(options.locale, {
      style: 'currency', currency: displayedCurrency,
      maximumFractionDigits: options.maximumFractionDigits ?? (ZERO_DECIMAL_CURRENCIES.includes(displayedCurrency) ? 0 : 2),
      minimumFractionDigits: options.minimumFractionDigits,
    }).format(displayedAmount);
  }, [convert, currency, rates]);

  const value = useMemo(() => ({
    currency, currencies, setCurrency, convert, convertTotal, formatMoney, formatIn, formatUsd,
    rates, rateDate, ratesAvailable,
  }), [currency, currencies, setCurrency, convert, convertTotal, formatMoney, rates, rateDate, ratesAvailable]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used inside CurrencyProvider');
  return context;
};

export const Money = ({ amount, sourceCurrency = 'USD', ...options }) => {
  const { formatMoney } = useCurrency();
  return formatMoney(amount, sourceCurrency, options);
};
